import * as React from 'react';
import {useTranslation} from 'react-i18next';
import {cn} from '../../lib/cn';
import {Skeleton} from '../../skeleton';
import type {SelectOption} from '../../lib/selector-option';
import {isOptionGroup, isOptionSelected as isOptionSelectedDefault} from './utils';
import {DefaultOption} from './slots/DefaultOption';
import type {SelectComponents} from './slots/types';
import type {
  SelectFormatGroupLabel,
  SelectFormatOptionLabel,
  SelectGroupedOption,
  SelectIsOptionDisabled,
  SelectIsOptionSelected,
  SelectValue,
} from './types';
import type {ReactNode} from 'react';

type SelectMenuProps<T, V extends SelectValue, L extends ReactNode> = {
  id: string;
  options: SelectOption<T, V, L>[];
  labelledBy: string;
  describedBy?: string;
  selectedValue?: SelectOption<T, V, L> | null;
  selectedValues?: SelectOption<T, V, L>[];
  highlightedIndex?: number;
  onOptionClick: (option: SelectOption<T, V, L>) => void;
  onOptionHover?: (index: number) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  disabled?: boolean;
  isOptionDisabled?: SelectIsOptionDisabled<T, V, L>;
  menuLoading?: boolean;
  menuClassName?: string;
  listRef?: React.RefObject<HTMLDivElement>;
  selectedOptionRef?: (index: number, node: HTMLButtonElement | null) => void;
  getOptionId: (index: number) => string;
  noOptionsMessage?: () => string | undefined;
  isMulti?: boolean;
  emptyContent?: React.ReactNode;
  footer?: React.ReactNode;
  components?: SelectComponents<T, V, L>;
  inputValue?: string;
  formatOptionLabel?: SelectFormatOptionLabel<T, V, L>;
  isOptionSelected?: SelectIsOptionSelected<T, V, L>;
  onMenuScrollToBottom?: (event: React.UIEvent<HTMLDivElement>) => void;
  groupedOptions?: SelectGroupedOption<T, V, L>[];
  formatGroupLabel?: SelectFormatGroupLabel<T, V, L>;
};

type OptionEntry<T, V extends SelectValue, L extends ReactNode> = {
  key: string;
  option: SelectOption<T, V, L>;
  flatIndex: number;
};

type MenuEntry<T, V extends SelectValue, L extends ReactNode> =
  | ({kind: 'option'} & OptionEntry<T, V, L>)
  | {
      kind: 'group';
      key: string;
      labelId: string;
      label: ReactNode;
      options: OptionEntry<T, V, L>[];
    };

function buildMenuEntries<T, V extends SelectValue, L extends ReactNode>(
  groupedOptions: SelectGroupedOption<T, V, L>[] | undefined,
  options: SelectOption<T, V, L>[],
  formatGroupLabel: SelectFormatGroupLabel<T, V, L> | undefined,
  listboxId: string,
): MenuEntry<T, V, L>[] {
  if (!groupedOptions || !groupedOptions.some(item => isOptionGroup(item))) {
    return options.map((option, index) => ({
      kind: 'option',
      key: `${String(option.value)}-${index}`,
      option,
      flatIndex: index,
    }));
  }

  const visibleValues = new Set<SelectValue>(options.map(option => option.value));
  const entries: MenuEntry<T, V, L>[] = [];
  let flatIndex = 0;
  let groupCount = 0;

  for (const item of groupedOptions) {
    if (isOptionGroup(item)) {
      const visible: OptionEntry<T, V, L>[] = [];
      for (const option of item.options) {
        if (!visibleValues.has(option.value)) continue;
        visible.push({
          key: `${String(option.value)}-${flatIndex}`,
          option,
          flatIndex,
        });
        flatIndex += 1;
      }
      if (visible.length === 0) continue;
      entries.push({
        kind: 'group',
        key: `__group-${groupCount}`,
        labelId: `${listboxId}-group-${groupCount}`,
        label: formatGroupLabel ? formatGroupLabel(item) : item.label,
        options: visible,
      });
      groupCount += 1;
    } else if (visibleValues.has(item.value)) {
      entries.push({
        kind: 'option',
        key: `${String(item.value)}-${flatIndex}`,
        option: item,
        flatIndex,
      });
      flatIndex += 1;
    }
  }
  return entries;
}

function SelectMenu<T, V extends SelectValue, L extends ReactNode>({
  id,
  options,
  labelledBy,
  describedBy,
  selectedValue,
  selectedValues,
  highlightedIndex,
  onOptionClick,
  onOptionHover,
  onKeyDown,
  disabled,
  isOptionDisabled,
  menuLoading,
  menuClassName,
  listRef,
  selectedOptionRef,
  getOptionId,
  noOptionsMessage,
  isMulti,
  emptyContent,
  footer,
  components,
  inputValue = '',
  formatOptionLabel,
  isOptionSelected,
  onMenuScrollToBottom,
  groupedOptions,
  formatGroupLabel,
}: SelectMenuProps<T, V, L>) {
  const {t} = useTranslation();
  const emptyMessage = noOptionsMessage?.() ?? t('no_options');
  const hasOptions = options.length > 0;
  const loadingMessage = t('loading');
  const Option = components?.Option ?? DefaultOption;
  const lastIndex = options.length - 1;
  const selectedList = React.useMemo(
    () => selectedValues ?? (selectedValue ? [selectedValue] : []),
    [selectedValues, selectedValue],
  );

  const entries = React.useMemo(
    () => buildMenuEntries(groupedOptions, options, formatGroupLabel, id),
    [groupedOptions, options, formatGroupLabel, id],
  );

  const renderOption = (entry: OptionEntry<T, V, L>) => {
    const {option, flatIndex, key} = entry;
    const isAction = typeof option.onAction === 'function';
    const isSelected = isAction
      ? false
      : isOptionSelected
        ? isOptionSelected(option, selectedList)
        : isOptionSelectedDefault(option, selectedValue, selectedValues);
    const isHighlighted = flatIndex === highlightedIndex;
    const optionDisabled = Boolean(
      disabled || option.isDisabled || isOptionDisabled?.(option),
    );
    return (
      <Option
        key={key}
        id={getOptionId(flatIndex)}
        option={option}
        index={flatIndex}
        isSelected={isSelected}
        isHighlighted={isHighlighted}
        isDisabled={optionDisabled}
        isLast={flatIndex === lastIndex}
        isMulti={Boolean(isMulti)}
        onClick={onOptionClick}
        onHover={onOptionHover ?? (() => undefined)}
        innerRef={node => selectedOptionRef?.(flatIndex, node)}
        inputValue={inputValue}
        selectedOptions={selectedList}
        formatOptionLabel={formatOptionLabel}
      />
    );
  };

  const wasAtBottomRef = React.useRef(false);
  const handleScroll = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!onMenuScrollToBottom) return;
      const target = event.currentTarget;
      const atBottom = target.scrollHeight - target.scrollTop - target.clientHeight <= 1;
      if (atBottom && !wasAtBottomRef.current) {
        onMenuScrollToBottom(event);
      }
      wasAtBottomRef.current = atBottom;
    },
    [onMenuScrollToBottom],
  );

  return (
    <div
      id={id}
      ref={listRef}
      role="listbox"
      tabIndex={-1}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      aria-multiselectable={isMulti}
      aria-busy={menuLoading}
      aria-activedescendant={
        highlightedIndex !== undefined && highlightedIndex >= 0
          ? getOptionId(highlightedIndex)
          : undefined
      }
      onKeyDown={onKeyDown}
      onScroll={onMenuScrollToBottom ? handleScroll : undefined}
      className={cn(
        'max-h-[var(--chekin-select-menu-max-height,300px)] overflow-y-auto overscroll-contain p-1.5 outline-none',
        '[scrollbar-color:var(--chekin-color-gray-3)_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[var(--chekin-color-gray-3)] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5',
        menuClassName,
      )}
    >
      {menuLoading && (
        <div role="status" aria-live="polite" className="flex flex-col gap-2 px-3 py-3">
          <span className="sr-only">{loadingMessage}</span>
          {Array.from({length: 3}).map((_, index) => (
            <Skeleton
              key={index}
              className="h-10 w-full rounded-md"
              style={{animationDelay: `${index * 120}ms`, opacity: 1 - index * 0.2}}
            />
          ))}
        </div>
      )}
      {!hasOptions &&
        !menuLoading &&
        (emptyContent ?? (
          <div className="px-3 py-3 text-left text-[14px] font-medium text-[var(--chekin-color-gray-1)]">
            {emptyMessage}
          </div>
        ))}
      {!menuLoading &&
        entries.map(entry => {
          if (entry.kind === 'group') {
            return (
              <div key={entry.key} role="group" aria-labelledby={entry.labelId}>
                <div
                  id={entry.labelId}
                  className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--chekin-color-gray-1)]"
                >
                  {entry.label}
                </div>
                {entry.options.map(renderOption)}
              </div>
            );
          }
          return renderOption(entry);
        })}
      {!menuLoading && footer}
    </div>
  );
}

export {SelectMenu};
export type {SelectMenuProps};
